import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Package, FileText, Warehouse, Warning, ChartBar, ArrowsClockwise,
  ClipboardText, Cube, Stack, TrendUp
} from '@phosphor-icons/react';

function StatCard({ label, value, icon: Icon, color, onClick }) {
  return (
    <Card
      data-testid={`inv-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`rounded-sm shadow-sm border-zinc-200 ${onClick ? 'cursor-pointer hover:border-orange-400 transition-colors' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded ${color} bg-opacity-10`}>
          <Icon size={22} weight="bold" className={color} />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</p>
          <p className="text-2xl font-light text-zinc-950">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function InventoryDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/api/inventory/dashboard-stats');
      setStats(data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load inventory stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <Layout><div className="py-12 text-center">Loading inventory dashboard...</div></Layout>;

  const cards = [
    { label: 'Total JOs', value: stats?.total_jos || 0, icon: FileText, color: 'text-blue-600', onClick: () => navigate('/inventory/job-orders') },
    { label: 'Not Processed', value: stats?.not_processed || 0, icon: ClipboardText, color: 'text-zinc-500', onClick: () => navigate('/inventory/job-orders?status=not_processed') },
    { label: 'PDF Uploaded', value: stats?.pdf_uploaded || 0, icon: FileText, color: 'text-amber-600', onClick: () => navigate('/inventory/job-orders?status=pdf_uploaded') },
    { label: 'Extracted', value: stats?.extracted || 0, icon: Package, color: 'text-purple-600', onClick: () => navigate('/inventory/job-orders?status=extracted') },
    { label: 'Verified', value: stats?.verified || 0, icon: Cube, color: 'text-cyan-600', onClick: () => navigate('/inventory/job-orders?status=verified') },
    { label: 'BOM Generated', value: stats?.bom_generated || 0, icon: Stack, color: 'text-emerald-600', onClick: () => navigate('/inventory/job-orders?status=bom_generated') },
    { label: 'Issued', value: stats?.issued || 0, icon: TrendUp, color: 'text-indigo-600', onClick: () => navigate('/inventory/job-orders?status=issued') },
    { label: 'Closed', value: stats?.closed || 0, icon: ChartBar, color: 'text-zinc-600', onClick: () => navigate('/inventory/job-orders?status=closed') },
  ];

  const stockAlerts = [
    { label: 'Total Materials', value: stats?.total_materials || 0, icon: Warehouse, color: 'text-blue-600' },
    { label: 'Below Min Stock', value: stats?.below_min_stock || 0, icon: Warning, color: 'text-red-600' },
    { label: 'Below Reorder', value: stats?.below_reorder || 0, icon: Warning, color: 'text-amber-600' },
  ];

  return (
    <Layout>
      <div className="space-y-6 pb-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Inventory Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-2">Inventory status overview across all Job Orders</p>
          </div>
          <Button data-testid="inv-refresh" onClick={fetchStats} variant="outline" className="h-10">
            <ArrowsClockwise size={16} weight="bold" className="mr-1.5" />
            Refresh
          </Button>
        </div>

        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">JO Inventory Status</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map(c => <StatCard key={c.label} {...c} />)}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Stock Alerts</h2>
          <div className="grid grid-cols-3 gap-3">
            {stockAlerts.map(c => <StatCard key={c.label} {...c} />)}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Button data-testid="nav-inv-jo" onClick={() => navigate('/inventory/job-orders')} className="h-14 bg-orange-600 hover:bg-orange-700 text-white font-bold">
            <FileText size={20} className="mr-2" /> Job Orders
          </Button>
          <Button data-testid="nav-inv-masters" onClick={() => navigate('/inventory/masters')} className="h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold">
            <Cube size={20} className="mr-2" /> Masters
          </Button>
          <Button data-testid="nav-inv-stock" onClick={() => navigate('/inventory/stock')} className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            <Warehouse size={20} className="mr-2" /> Stock Operations
          </Button>
          <Button data-testid="nav-inv-procurement" onClick={() => navigate('/inventory/procurement')} className="h-14 bg-purple-600 hover:bg-purple-700 text-white font-bold">
            <ChartBar size={20} className="mr-2" /> Procurement Req
          </Button>
        </div>
      </div>
    </Layout>
  );
}
